import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAnnouncementListings } from '@/hooks/useAnnouncementListings';

interface Announcement {
  id: string;
  title: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  breeder_id: string;
}

interface BreederAnnouncementsProps {
  announcements: Announcement[];
  loading?: boolean;
}

const AnnouncementCard: React.FC<{ announcement: Announcement }> = ({ announcement }) => {
  const { data: listings = [], isLoading } = useAnnouncementListings({ 
    announcementId: announcement.id 
  });

  return (
    <Card className="bg-green-50 border-green-200">
      <CardContent className="p-4">
        <div className="md:flex md:items-start md:justify-between">
          <div className="flex-1">
            <h4 className="font-semibold text-lg mb-2">{announcement.title}</h4>
            {announcement.description && (
              <p className="text-gray-600 leading-relaxed mb-4 md:mb-0">{announcement.description}</p>
            )}
          </div>
          
          {!isLoading && listings.length > 0 && (
            <div className="mt-4 md:mt-0 md:ml-6 md:flex-shrink-0 hidden md:block">
              <Link to={`/announcement/${announcement.id}`}>
                <Button className="bg-green-700 hover:bg-green-800 text-white">
                  See more
                </Button>
              </Link>
            </div>
          )}
        </div>
        
        {/* Mobile button */}
        {!isLoading && listings.length > 0 && (
          <div className="mt-4 md:hidden">
            <Link to={`/announcement/${announcement.id}`}>
              <Button className="bg-green-700 hover:bg-green-800 text-white w-full">
                See more
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const BreederAnnouncements: React.FC<BreederAnnouncementsProps> = ({ 
  announcements, 
  loading 
}) => {
  if (loading) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Upcoming Auctions</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-700"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Upcoming Auctions</h2>
      <div className="space-y-4">
        {announcements.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p>No announcements at this time.</p>
          </div>
        ) : (
          announcements.map((announcement) => (
            <AnnouncementCard key={announcement.id} announcement={announcement} />
          ))
        )}
      </div>
    </div>
  );
};
